import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  GroupBuyOnlineOperatorDecisionSchema,
  GroupBuyOnlineOperatorModeSchema,
} from "./group-buy-operator-decision.js";
import { isOrderNumberForOrderId, OrderNumberSchema } from "./order-number.js";

const REDEMPTION_ID_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$";
const GROUP_BUY_REDEMPTION_NUMBER_PATTERN = /^HX-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

const RedemptionIdSchema = Type.String({
  minLength: 12,
  maxLength: 128,
  pattern: REDEMPTION_ID_PATTERN,
});
const CanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const NullableIdentifierSchema = Type.Union([RedemptionIdSchema, Type.Null()]);

export const GroupBuyRedemptionNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^HX-[0-9]{8}-[0-9A-F]{12}$",
});
export type GroupBuyRedemptionNumber = Static<typeof GroupBuyRedemptionNumberSchema>;

export const GroupBuyOnlineRedemptionStatusSchema = Type.Union([
  Type.Literal("PREPARED"),
  Type.Literal("VALIDATION_UNKNOWN"),
  Type.Literal("VALIDATED"),
  Type.Literal("REDEMPTION_UNKNOWN"),
  Type.Literal("REDEEMED"),
  Type.Literal("COMPLETED"),
  Type.Literal("COMPENSATED"),
  Type.Literal("NEEDS_REVIEW"),
  Type.Literal("REJECTED"),
]);
export type GroupBuyOnlineRedemptionStatus = Static<typeof GroupBuyOnlineRedemptionStatusSchema>;

export const StartGroupBuyOnlineRedemptionRequestSchema = Type.Object(
  {
    redemptionId: RedemptionIdSchema,
    tableId: Type.String({ minLength: 1, maxLength: 128 }),
    expectedTableRevision: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    channelId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedChannelVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    packageId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedPackageVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    voucherCode: Type.String({ minLength: 4, maxLength: 128 }),
    note: Type.String({ maxLength: 200 }),
    customerRequestId: Type.Optional(RedemptionIdSchema),
    expectedCustomerRequestVersion: Type.Optional(
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    ),
    operatorMode: Type.Optional(GroupBuyOnlineOperatorModeSchema),
    takeoverReason: Type.Optional(
      Type.Union([
        Type.String({
          minLength: 2,
          maxLength: 500,
          pattern: "^\\S(?:[\\s\\S]*\\S)?$",
        }),
        Type.Null(),
      ]),
    ),
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type StartGroupBuyOnlineRedemptionRequest = Static<
  typeof StartGroupBuyOnlineRedemptionRequestSchema
>;

export const ResumeGroupBuyOnlineRedemptionRequestSchema = Type.Object(
  {
    voucherCode: Type.String({ minLength: 4, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type ResumeGroupBuyOnlineRedemptionRequest = Static<
  typeof ResumeGroupBuyOnlineRedemptionRequestSchema
>;

export const GroupBuyOnlineRedemptionRecordSchema = Type.Object(
  {
    contractVersion: Type.Union([Type.Literal("1.0.0"), Type.Literal("1.1.0")]),
    entryMode: Type.Union([Type.Literal("ONLINE"), Type.Literal("OFFLINE_PRECHECK")]),
    redemptionId: RedemptionIdSchema,
    redemptionNumber: GroupBuyRedemptionNumberSchema,
    businessDate: BusinessDateSchema,
    tableId: Type.String({ minLength: 1, maxLength: 128 }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    expectedTableRevision: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    expectedOperatingConfigurationVersion: Type.Integer({
      minimum: 1,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    channelSnapshot: Type.Object(
      {
        id: Type.String({ minLength: 1, maxLength: 64 }),
        provider: Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]),
        displayName: Type.String({ minLength: 1, maxLength: 40 }),
        version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      },
      { additionalProperties: false },
    ),
    packageSnapshot: Type.Object(
      {
        id: Type.String({ minLength: 1, maxLength: 64 }),
        name: Type.String({ minLength: 1, maxLength: 80 }),
        version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
        includedMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
        voucherCoveredFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
        customerSupplementFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
        expectedSettlementFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
        expectedFeeFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
        expectedSubsidyFen: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
        components: Type.Array(
          Type.Object(
            {
              kind: Type.Union([Type.Literal("PRODUCT"), Type.Literal("SERVICE")]),
              referenceId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
              name: Type.String({ minLength: 1, maxLength: 80 }),
              quantity: Type.Integer({ minimum: 1, maximum: 99 }),
            },
            { additionalProperties: false },
          ),
          { maxItems: 30 },
        ),
      },
      { additionalProperties: false },
    ),
    voucherCodeSuffix: Type.String({ minLength: 2, maxLength: 4, pattern: "^[^\\s]+$" }),
    status: GroupBuyOnlineRedemptionStatusSchema,
    providerStatus: Type.Union([
      Type.Literal("NOT_CALLED"),
      Type.Literal("VALID"),
      Type.Literal("INVALID"),
      Type.Literal("USED"),
      Type.Literal("NOT_REDEEMABLE"),
      Type.Literal("REDEEMED"),
      Type.Literal("REVOKED"),
      Type.Literal("REFUNDED"),
      Type.Literal("UNKNOWN"),
    ]),
    providerReference: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    providerEvidence: Type.Union([
      Type.Literal("SANDBOX"),
      Type.Literal("STORE_ACCEPTED"),
      Type.Null(),
    ]),
    sessionId: NullableIdentifierSchema,
    orderId: NullableIdentifierSchema,
    orderNumber: Type.Union([OrderNumberSchema, Type.Null()]),
    fulfillmentId: NullableIdentifierSchema,
    reviewWorkItemId: NullableIdentifierSchema,
    failureCode: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    failureMessage: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    note: Type.String({ maxLength: 200 }),
    actorId: Type.String({ minLength: 1, maxLength: 128 }),
    customerRequestId: Type.Optional(Type.Union([RedemptionIdSchema, Type.Null()])),
    customerRequestNumber: Type.Optional(
      Type.Union([Type.String({ pattern: "^TG-[0-9]{8}-[0-9A-F]{12}$" }), Type.Null()]),
    ),
    operatorDecision: Type.Optional(GroupBuyOnlineOperatorDecisionSchema),
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyOnlineRedemptionRecord = Static<typeof GroupBuyOnlineRedemptionRecordSchema>;

export const CreateGroupBuyOfflinePrecheckRequestSchema =
  StartGroupBuyOnlineRedemptionRequestSchema;
export type CreateGroupBuyOfflinePrecheckRequest = StartGroupBuyOnlineRedemptionRequest;

export const GroupBuyOnlineRedemptionCapabilitySchema = Type.Object(
  {
    provider: Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]),
    enabled: Type.Boolean(),
    validate: Type.Boolean(),
    redeem: Type.Boolean(),
    query: Type.Boolean(),
    revoke: Type.Boolean(),
    evidence: Type.Union([
      Type.Literal("NOT_VERIFIED"),
      Type.Literal("SANDBOX"),
      Type.Literal("STORE_ACCEPTED"),
    ]),
  },
  { additionalProperties: false },
);
export type GroupBuyOnlineRedemptionCapability = Static<
  typeof GroupBuyOnlineRedemptionCapabilitySchema
>;
export const GroupBuyOnlineRedemptionCapabilityListSchema = Type.Array(
  GroupBuyOnlineRedemptionCapabilitySchema,
  { minItems: 2, maxItems: 2 },
);
export type GroupBuyOnlineRedemptionCapabilityList = Static<
  typeof GroupBuyOnlineRedemptionCapabilityListSchema
>;

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveGroupBuyRedemptionNumber(
  businessDate: string,
  redemptionId: string,
): GroupBuyRedemptionNumber {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!new RegExp(REDEMPTION_ID_PATTERN, "u").test(redemptionId)) {
    throw new TypeError("redemptionId must be a safe identifier with at least 12 characters");
  }
  return `HX-${businessDate.replaceAll("-", "")}-${deriveFortyEightBitHex(
    `${businessDate}\0${redemptionId}`,
  )}`;
}

export function isGroupBuyRedemptionNumber(value: unknown): value is GroupBuyRedemptionNumber {
  if (!Value.Check(GroupBuyRedemptionNumberSchema, value)) return false;
  const match = GROUP_BUY_REDEMPTION_NUMBER_PATTERN.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}

export function isGroupBuyOnlineRedemptionRecord(
  value: unknown,
): value is GroupBuyOnlineRedemptionRecord {
  if (!Value.Check(GroupBuyOnlineRedemptionRecordSchema, value)) return false;
  const record = value as GroupBuyOnlineRedemptionRecord;
  if (
    (record.contractVersion === "1.0.0" && record.operatorDecision !== undefined) ||
    (record.contractVersion === "1.1.0" && record.operatorDecision === undefined) ||
    (record.operatorDecision !== undefined &&
      record.operatorDecision.cashShiftBusinessDate !== record.businessDate) ||
    !isRealBusinessDate(record.businessDate) ||
    record.redemptionNumber !==
      deriveGroupBuyRedemptionNumber(record.businessDate, record.redemptionId) ||
    Date.parse(record.updatedAt) < Date.parse(record.createdAt) ||
    (record.customerRequestId === null) !== (record.customerRequestNumber === null)
  ) {
    return false;
  }
  const hasProviderFact = record.providerReference !== null && record.providerEvidence !== null;
  const hasLocalCompletion =
    record.sessionId !== null &&
    record.orderId !== null &&
    record.orderNumber !== null &&
    record.fulfillmentId !== null;
  const hasNoLocalCompletion =
    record.sessionId === null &&
    record.orderId === null &&
    record.orderNumber === null &&
    record.fulfillmentId === null;
  const rejectedBeforeProviderCall =
    record.providerStatus === "NOT_CALLED" && !hasProviderFact && record.reviewWorkItemId === null;
  const rejectedByProvider =
    ["INVALID", "USED", "NOT_REDEEMABLE", "REVOKED", "REFUNDED"].includes(record.providerStatus) &&
    hasProviderFact &&
    ((record.entryMode === "ONLINE" && record.reviewWorkItemId === null) ||
      (record.entryMode === "OFFLINE_PRECHECK" && record.reviewWorkItemId !== null));

  switch (record.status) {
    case "PREPARED":
      return (
        record.providerStatus === "NOT_CALLED" &&
        !hasProviderFact &&
        hasNoLocalCompletion &&
        record.reviewWorkItemId === null &&
        record.failureCode === null &&
        record.failureMessage === null
      );
    case "VALIDATION_UNKNOWN":
    case "REDEMPTION_UNKNOWN":
      return (
        record.providerStatus === "UNKNOWN" &&
        record.providerEvidence !== null &&
        hasNoLocalCompletion &&
        record.reviewWorkItemId === null
      );
    case "VALIDATED":
      return record.providerStatus === "VALID" && hasProviderFact && hasNoLocalCompletion;
    case "REDEEMED":
      return record.providerStatus === "REDEEMED" && hasProviderFact && hasNoLocalCompletion;
    case "COMPLETED":
      return (
        record.providerStatus === "REDEEMED" &&
        hasProviderFact &&
        hasLocalCompletion &&
        record.orderId !== null &&
        record.orderNumber !== null &&
        isOrderNumberForOrderId(record.orderNumber, record.orderId, record.businessDate) &&
        record.reviewWorkItemId === null &&
        record.failureCode === null &&
        record.failureMessage === null
      );
    case "COMPENSATED":
      return (
        record.providerStatus === "REVOKED" &&
        hasProviderFact &&
        hasNoLocalCompletion &&
        record.failureCode !== null &&
        record.failureMessage !== null
      );
    case "NEEDS_REVIEW":
      return (
        (record.providerStatus === "UNKNOWN" || record.providerStatus === "REDEEMED") &&
        record.providerEvidence !== null &&
        hasNoLocalCompletion &&
        record.reviewWorkItemId !== null &&
        record.failureCode !== null &&
        record.failureMessage !== null
      );
    case "REJECTED":
      return (
        hasNoLocalCompletion &&
        (rejectedBeforeProviderCall || rejectedByProvider) &&
        record.failureCode !== null &&
        record.failureMessage !== null
      );
  }
}

export function isStartGroupBuyOnlineRedemptionRequest(
  value: unknown,
): value is StartGroupBuyOnlineRedemptionRequest {
  if (!Value.Check(StartGroupBuyOnlineRedemptionRequestSchema, value)) return false;
  return (
    (value.customerRequestId === undefined) === (value.expectedCustomerRequestVersion === undefined)
  );
}

export function isResumeGroupBuyOnlineRedemptionRequest(
  value: unknown,
): value is ResumeGroupBuyOnlineRedemptionRequest {
  return Value.Check(ResumeGroupBuyOnlineRedemptionRequestSchema, value);
}
